import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG } from '../config/api.config';

interface User {
  id: string;
  username: string;
  name: string;
  role: string;
  email?: string;
}

interface AuthResponse {
  success?: boolean;
  token: string;
  refreshToken: string;
  user: User;
  requires2FA?: boolean;
  message?: string;
}

export type LoginResult =
  | { ok: true }
  | { ok: false; requires2FA: true; message?: string }
  | { ok: false; requires2FA?: false; message: string };

// Aumentar timeout base para todas as requisições
const DEFAULT_TIMEOUT = 30000; // 30 segundos

// Criar instância do axios com configuração base
const authApi = axios.create({
  baseURL: API_CONFIG.BACKEND.BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Importante para enviar cookies e autenticação
});

// Função de log para debug
const logDebug = (message: string, data?: any) => {
  console.log(`[AUTH] ${message}`, data || '');
};

export const authService = {
  // Getters para acessar valores do localStorage
  get token() {
    return localStorage.getItem('token');
  },
  get refreshToken() {
    return localStorage.getItem('refreshToken');
  },
  get user(): User | null {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.error('Erro ao analisar JSON do usuário:', e);
      return null;
    }
  },

  // Estado para controle de refresh de token
  refreshInProgress: false,
  refreshPromise: null as Promise<boolean> | null,

  // Funções para autenticação e gerenciamento de sessão
  async login(
    usernameOrEmail: string,
    password: string,
    twoFactorCode?: string
  ): Promise<LoginResult> {
    try {
      logDebug(`Tentando login para: ${usernameOrEmail}`);

      const isEmail = usernameOrEmail.includes('@');

      const payload: Record<string, string | undefined> = {
        username: !isEmail ? usernameOrEmail : undefined,
        email: isEmail ? usernameOrEmail : undefined,
        password
      };

      if (twoFactorCode && twoFactorCode.trim().length > 0) {
        payload.twoFactorCode = twoFactorCode.trim();
      }

      logDebug('Enviando requisição de login');
      const response = await authApi.post<AuthResponse>('/api/auth/login', payload);
      const data = response.data;

      // Backend pode responder 200 com { success: false, requires2FA: true }
      if (data && data.success === false && data.requires2FA) {
        logDebug('Backend solicitou 2FA');
        return { ok: false, requires2FA: true, message: data.message };
      }

      const { token, refreshToken, user } = data;
      if (!token || !user) {
        return {
          ok: false,
          message: data?.message || 'Resposta inválida do servidor.'
        };
      }

      logDebug('Login bem-sucedido, armazenando tokens');
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      logDebug('Login concluído com sucesso');
      return { ok: true };
    } catch (error) {
      console.error('=== LOGIN ERROR DEBUG ===');
      console.error('Erro no login:', error);
      let message = 'Usuário ou senha inválidos';
      let requires2FA = false;
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as AuthResponse | undefined;
        if (data?.requires2FA) {
          requires2FA = true;
        }
        if (data?.message) {
          message = data.message;
        }
      }
      console.error('=== LOGIN ERROR DEBUG FIM ===');
      if (requires2FA) {
        return { ok: false, requires2FA: true, message };
      }
      return { ok: false, message };
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    try {
      const response = await authApi.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      return response.data.success;
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      return false;
    }
  },

  // Função de logout totalmente refatorada para evitar problemas com propriedades somente leitura
  logout() {
    try {
      logDebug('Realizando logout');
      
      // Limpar localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Limpar cabeçalhos de autorização
      if (authApi.defaults.headers && authApi.defaults.headers.common) {
        delete authApi.defaults.headers.common['Authorization'];
      }
      
      logDebug('Logout concluído, redirecionando...');
      
      // Forçar redirecionamento para a página de login
      setTimeout(() => {
        window.location.href = '/login';
      }, 10);
    } catch (error) {
      console.error('Erro durante logout:', error);
      // Tentar redirecionar mesmo com erro
      window.location.href = '/login';
    }
  },

  isAuthenticated(): boolean {
    // Verificar se os tokens existem no localStorage e têm conteúdo válido
    const token = localStorage.getItem('token');
    const userJson = localStorage.getItem('user');
    
    // Verificar se o token existe e tem um formato válido (não vazio)
    const hasValidToken = !!token && token.length > 10;
    
    // Verificar se o user existe e é um JSON válido
    let hasValidUser = false;
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        hasValidUser = !!user && !!user.id;
      } catch (e) {
        console.error('Erro ao analisar JSON do usuário:', e);
      }
    }
    
    const isAuth = hasValidToken && hasValidUser;
    logDebug(`Verificação de autenticação: ${isAuth ? 'Autenticado' : 'Não autenticado'}`);
    return isAuth;
  },

  // Função refatorada para usar diretamente localStorage em vez de this.user
  getUser(): User | null {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.error('Erro ao analisar JSON do usuário:', e);
      return null;
    }
  },

  // Função refatorada para usar diretamente localStorage em vez de this.token
  getToken(): string | null {
    const token = localStorage.getItem('token');
    logDebug(`Token obtido do localStorage: ${token ? 'Token encontrado' : 'Token não encontrado'}`);
    return token;
  },

  async refreshAuthToken(): Promise<boolean> {
    // Se já estiver um refresh em andamento, retorna a mesma Promise
    if (this.refreshInProgress && this.refreshPromise) {
      logDebug('Refresh já em andamento, retornando Promise existente');
      return this.refreshPromise;
    }

    // Marcar que um refresh está em andamento
    this.refreshInProgress = true;
    
    // Criar uma nova Promise para o processo de refresh
    this.refreshPromise = new Promise<boolean>(async (resolve) => {
      try {
        logDebug('Iniciando renovação de token');
        
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          logDebug('Refresh token não encontrado, realizando limpeza');
          
          // Limpar tokens sem chamar this.logout() para evitar recursão
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          delete authApi.defaults.headers.common['Authorization'];
          
          logDebug('Tokens removidos, redirecionando para login');
          
          // Redirecionar para login após pequeno delay
          setTimeout(() => {
            window.location.href = '/login?session=expired';
          }, 100);
          
          resolve(false);
          return;
        }

        logDebug('Enviando requisição de refresh com timeout estendido');
        
        // Aumentar o timeout específico para operação de refresh
        const response = await authApi.post<{ token: string; refreshToken: string }>('/api/auth/refresh', 
          { refreshToken },
          { timeout: DEFAULT_TIMEOUT * 2 } // Timeout dobrado para refresh
        );

        const { token, refreshToken: newRefreshToken } = response.data;
        
        logDebug('Refresh bem-sucedido, atualizando tokens');
        
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        
        authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        logDebug('Tokens atualizados com sucesso');
        resolve(true);
      } catch (error) {
        console.error('=== REFRESH TOKEN ERROR DEBUG ===');
        console.error('Erro ao renovar token:', error);
        
        if (axios.isAxiosError(error)) {
          console.error('Detalhes do erro de refresh:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            headers: error.response?.headers,
            url: error.config?.url,
            method: error.config?.method
          });
        }
        
        console.error('=== REFRESH TOKEN ERROR DEBUG FIM ===');
        
        logDebug('Falha no refresh, realizando limpeza de tokens');
        
        // Limpar tokens sem chamar this.logout() para evitar recursão
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        delete authApi.defaults.headers.common['Authorization'];
        
        // Redirecionar para login apenas se estiver em ambiente de navegador
        if (typeof window !== 'undefined') {
          window.location.href = '/login?expired=true';
        }
        
        resolve(false);
      } finally {
        // Limpar estado de refresh
        this.refreshInProgress = false;
        this.refreshPromise = null;
      }
    });

    return this.refreshPromise;
  },

  setupInterceptors() {
    logDebug('Configurando interceptors');
    
    // Interceptor para adicionar token em todas as requisições
    authApi.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${token}`
          };
        }
        return config;
      },
      (error) => {
        logDebug('Erro na requisição:', error.message);
        return Promise.reject(error);
      }
    );

    // Interceptor para tratar erros de autenticação
    authApi.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Se o erro for 401 e não for uma tentativa de refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          logDebug('Resposta 401 recebida, tentando renovar token');
          originalRequest._retry = true;
          
          try {
            // Tenta renovar o token
            const success = await this.refreshAuthToken();
            if (success) {
              logDebug('Token renovado com sucesso, repetindo requisição original');
              // Repete a requisição original com o novo token
              const token = localStorage.getItem('token');
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return authApi(originalRequest);
              }
            }
          } catch (refreshError) {
            logDebug('Erro ao renovar token, redirecionando para login');
            console.error('Erro ao renovar token:', refreshError);
          }
          
          // Se não conseguir renovar, limpa tokens sem chamar this.logout()
          logDebug('Falha na renovação, limpando tokens e redirecionando');
          
          // Limpar tokens diretamente para evitar loops recursivos
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          delete authApi.defaults.headers.common['Authorization'];
          
          // Redirecionar para login apenas se estiver em ambiente de navegador
          if (typeof window !== 'undefined') {
            window.location.href = '/login?session=expired';
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    logDebug('Interceptors configurados com sucesso');
  }
};

// Configurar interceptors ao inicializar
authService.setupInterceptors();

// Exporta o authApi para uso interno (apenas neste módulo)
// Não exportamos como default para evitar dependências circulares
export { authApi }; 